import { useParams, useNavigate, useEffect } from 'react-router-dom';
import { useEffect } from 'react';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';

interface PackageDetail {
  id: string;
  name: string;
  subtitle: string;
  price: string;
  priceNote: string;
  heroImage: string;
  description: string;
  features: string[];
  whoThisIsFor: {
    title: string;
    description: string;
    idealFor: string[];
  };
  whatToExpect: {
    title: string;
    description: string;
    breakdown: Array<{
      phase: string;
      duration: string;
      focus: string[];
    }>;
  };
  athleticTesting?: {
    title: string;
    subtitle: string;
    metrics: Array<{
      name: string;
      icon: string;
      description: string;
      unit: string;
    }>;
  };
  additionalBenefits: string[];
}

const packageData: Record<string, PackageDetail> = {
  assessment: {
    id: 'assessment',
    name: 'ASSESSMENT - BASEBALL',
    subtitle: 'Individual Sessions',
    price: '$500',
    priceNote: 'FREE with 6-month+ commitment',
    heroImage: 'https://public.readdy.ai/ai/img_res/a8fa5e276463dcd0ec0cd87e74f2b38f.jpg',
    description: 'Our comprehensive assessment session provides athletes with a complete baseline evaluation of their current performance metrics, movement patterns, and arm health. This data-driven approach gives you the insights needed to understand where you stand and what areas need development.',
    features: [
      'Single session booking with flexible scheduling, Plan 2 hours for assessment and review.',
      'Complete initial movement screen and biomechanical analysis',
      'Velocity measurement, Ground Force production and arm health evaluation',
      'Personalized session feedback from certified coaches',
      'All data collected supplied to player in detailed report, emailed 1 week after initial testing',
      'Video analysis of throwing mechanics',
      'Comparison to peer group and age-appropriate benchmarks',
      'Recommended training pathway based on results',
    ],
    whoThisIsFor: {
      title: 'Who This Is For',
      description: 'Perfect for athletes who want to establish their baseline metrics and understand their current standing.',
      idealFor: [
        'Athletes new to performance training looking to understand their starting point',
        'Players preparing for tryouts or showcases who need current metrics',
        'Athletes considering a long-term training commitment and want to experience our facility',
        'Players recovering from injury who need a movement and arm health check',
        'Parents who want objective data on their athlete\'s development',
        'Athletes that simply want quantifiable metrics',
      ],
    },
    whatToExpect: {
      title: 'What to Expect',
      description: 'Your assessment session is a comprehensive 90-minute evaluation designed to give you complete insight into your current performance level.',
      breakdown: [
        {
          phase: 'Intake & Goal Setting',
          duration: '15 minutes',
          focus: [
            'Review athletic history and injury background',
            'Discuss current goals and training experience',
            'Establish baseline expectations',
          ],
        },
        {
          phase: 'Movement Screen',
          duration: '20 minutes',
          focus: [
            'Functional movement assessment',
            'Mobility and stability evaluation',
            'Identify movement limitations or compensations',
          ],
        },
        {
          phase: 'Throwing Assessment',
          duration: '45 minutes',
          focus: [
            'Velocity measurement with Rapsodo/TrackMan',
            'Video capture of throwing mechanics',
            'Arm health and stress evaluation',
            'Spin rate and ball flight metrics',
            'Ground force production for hitting and pitching',
          ],
        },
        {
          phase: 'Data Review & Recommendations',
          duration: '30 - 45 minutes',
          focus: [
            'Review all collected data and metrics',
            'Compare to peer benchmarks',
            'Discuss findings and areas for improvement',
            'Receive personalized training recommendations',
          ],
        },
      ],
    },
    athleticTesting: {
      title: 'Athletic Screen',
      subtitle: '30 minutes',
      metrics: [
        {
          name: 'Vertical Jump',
          icon: 'ri-arrow-up-double-line',
          description: 'Measures lower-body explosive power and fast-twitch muscle activation using a jump mat or Vertec.',
          unit: 'inches',
        },
        {
          name: '5-10-5 Shuttle',
          icon: 'ri-timer-flash-line',
          description: 'Laser-timed lateral quickness and change-of-direction test — a key metric for all field positions.',
          unit: 'seconds (laser timed)',
        },
        {
          name: 'Broad Jump',
          icon: 'ri-footprint-line',
          description: 'Horizontal power output test measuring hip extension strength and athletic projection.',
          unit: 'inches',
        },
      ],
    },
    additionalBenefits: [
      'Detailed written report with all metrics and video links',
      'All Data Emailed to your personal email.',
      'One follow-up consultation call within 30 days',
      'Baseline will qualify for program acceptance&nbsp;',
      'Assessment fee waived with 6-month program commitment',
    ],
  },
  junior: {
    id: 'junior',
    name: '10u - 13u',
    subtitle: 'MONTHLY JR. HIGH PROGRAM',
    price: '$325',
    priceNote: 'Billed Monthly',
    heroImage: 'https://readdy.ai/api/search-image?query=Young%20baseball%20athletes%20training%20in%20modern%20sports%20facility%2C%20junior%20players%20doing%20agility%20drills%20and%20fundamental%20movement%20exercises%2C%20bright%20energetic%20training%20environment%20with%20coaches%20supervising%2C%20youth%20sports%20development%20program%20with%20proper%20form%20and%20technique%20focus%2C%20clean%20professional%20facility&width=1920&height=600&seq=juniordetail001&orientation=landscape',
    description: 'Our junior development program is specifically designed for young athletes ages 10-13 who are building the foundation for high school varsity sports. This structured monthly program focuses on proper movement patterns, age-appropriate strength development, and fundamental skill acquisition in a safe and supportive environment.',
    features: [
      'Free full initial assessment to establish baseline',
      'Quarterly assessments to track progress and development',
      'Fundamental arm care and throwing mechanics development',
      'Speed and agility training appropriate for age group',
      'Age-appropriate strength development and conditioning',
      'Mental performance foundation and confidence building',
      'Proprioception and impulse control development',
      'Two training days per week (8 sessions monthly)',
      'Small group training with individualized attention',
      'Parent progress reports and communication',
    ],
    whoThisIsFor: {
      title: 'Who This Is For',
      description: 'Designed for young athletes who are serious about developing proper fundamentals and preparing for competitive high school athletics.',
      idealFor: [
        'Athletes ages 10-13 preparing for middle school or high school sports',
        'Young players who want to develop proper mechanics before bad habits form',
        'Athletes transitioning from recreational to competitive leagues',
        'Players who need structured training in a peer group environment',
        'Young athletes looking to build confidence and mental toughness',
        'Parents who want their child to train safely with age-appropriate methods',
      ],
    },
    whatToExpect: {
      title: 'What to Expect',
      description: 'Each month includes 8 structured training sessions (2 per week) focused on building a complete athletic foundation.',
      breakdown: [
        {
          phase: 'Month 1: Foundation & Assessment',
          duration: '4 weeks',
          focus: [
            'Complete initial assessment and baseline testing',
            'Introduction to proper warm-up and arm care routines',
            'Basic movement patterns and body awareness',
            'Fundamental throwing mechanics',
            'Speed and agility basics',
          ],
        },
        {
          phase: 'Months 2-3: Skill Development',
          duration: '8 weeks',
          focus: [
            'Progressive arm care and throwing program',
            'Age-appropriate strength exercises',
            'Speed, agility, and coordination drills',
            'Mental performance introduction',
            'Quarterly assessment and progress review',
          ],
        },
        {
          phase: 'Ongoing: Continuous Improvement',
          duration: 'Monthly',
          focus: [
            'Progressive overload in all training areas',
            'Quarterly benchmark testing',
            'Refinement of mechanics and movement quality',
            'Building consistency and work ethic',
            'Preparation for high school varsity athletics',
          ],
        },
      ],
    },
    additionalBenefits: [
      'Access to facility during open training hours',
      'Monthly parent-coach check-ins',
      'Educational resources on youth athlete development',
      'Injury prevention education and monitoring',
      'Team-building activities and peer support',
      'Flexible scheduling for school and travel ball commitments',
    ],
  },
  'middle-school': {
    id: 'middle-school',
    name: '14u - 16u',
    subtitle: 'MONTHLY MIDDLE SCHOOL PROGRAM',
    price: '$425',
    priceNote: 'Billed Monthly',
    heroImage: 'https://readdy.ai/api/search-image?query=Middle%20school%20baseball%20athletes%20training%20in%20a%20modern%20professional%20sports%20facility%2C%20teenage%20players%20doing%20speed%20and%20agility%20drills%20on%20turf%20with%20coaches%20guiding%20them%2C%20energetic%20training%20environment%20with%20teal%20accent%20lighting%2C%20focused%20young%20athletes%20in%20athletic%20gear%20performing%20movement%20exercises%2C%20clean%20premium%20facility%20with%20dark%20walls%20and%20bright%20field%20lighting&width=1920&height=600&seq=middleschooldetail001&orientation=landscape',
    description: 'The 14u–16u program is the critical bridge between youth development and high school varsity competition. Athletes in this age group are entering their most important physical development window — and we make sure every rep counts. This program builds the strength, speed, arm health, and mental foundation needed to compete at the next level.',
    features: [
      'Free full initial assessment to establish baseline',
      'Quarterly assessments to track progress and development',
      'Arm care & velocity development program',
      'Speed & agility training tailored to age group',
      'Strength & power foundation training',
      'Mental performance coaching and focus development',
      'Advanced movement screening and biomechanical analysis',
      'Two training days per week (8 sessions monthly)',
      'Classes Monday & Wednesday, Tuesday & Thursday',
      'Small group training — limited to 10 athletes per class, 2 classes',
      'Parent progress reports and coach communication',
      'Preparation for high school varsity tryouts and showcases',
    ],
    whoThisIsFor: {
      title: 'Who This Is For',
      description: 'Built for athletes ages 14–16 who are serious about making the jump to high school varsity and want to develop the physical and mental tools to compete at that level.',
      idealFor: [
        'Athletes ages 14–16 preparing for high school varsity sports',
        'Players who want to develop proper mechanics before bad habits become permanent',
        'Athletes transitioning from youth recreational leagues to competitive high school programs',
        'Players who need structured group training with individualized coaching attention',
        'Young athletes looking to build confidence, mental toughness, and competitive edge',
        'Parents who want their athlete training safely with age-appropriate, science-backed methods',
      ],
    },
    whatToExpect: {
      title: 'What to Expect',
      description: 'Each month includes 8 structured training sessions (2 per week) designed to systematically build every dimension of your athletic performance.',
      breakdown: [
        {
          phase: 'Month 1: Assessment & Foundation',
          duration: '4 weeks',
          focus: [
            'Complete initial assessment and baseline metric testing',
            'Introduction to proper warm-up and arm care routines',
            'Fundamental movement patterns and body awareness',
            'Throwing mechanics evaluation and initial corrections',
            'Speed and agility baseline testing',
            'Mental performance introduction and goal setting',
          ],
        },
        {
          phase: 'Months 2–3: Skill & Strength Development',
          duration: '8 weeks',
          focus: [
            'Progressive arm care and velocity development program',
            'Age-appropriate strength and power exercises',
            'Speed, agility, and lateral quickness drills',
            'Advanced movement screening and correction',
            'Mental performance skills — focus, confidence, and composure',
            'Quarterly assessment and progress review',
          ],
        },
        {
          phase: 'Ongoing: Performance & Varsity Preparation',
          duration: 'Monthly',
          focus: [
            'Progressive overload across all training areas',
            'Quarterly benchmark testing and metric tracking',
            'Refinement of mechanics and movement quality',
            'Showcase and high school tryout preparation',
            'Building consistency, work ethic, and competitive mindset',
            'Transition planning toward the High School / College program',
          ],
        },
      ],
    },
    additionalBenefits: [
      'Monthly parent-coach check-ins and progress updates',
      'Educational resources on athlete development and nutrition',
      'Injury prevention education and ongoing monitoring',
      'Peer group training environment for motivation and accountability',
      'Scheduling to accommodate school and travel ball commitments.&nbsp;',
      'Priority pathway into the High School / College program when ready',
    ],
  },
  elite: {
    id: 'elite',
    name: 'High School Program',
    subtitle: '14u high school eligible',
    price: 'Contact Us',
    priceNote: 'Most comprehensive plan',
    heroImage: 'https://readdy.ai/api/search-image?query=Elite%20baseball%20training%20session%20with%20advanced%20technology%2C%20athlete%20using%20Rapsodo%20and%20TrackMan%20equipment%20in%20professional%20facility%2C%20high%20performance%20training%20environment%20with%20data%20displays%20and%20modern%20equipment%2C%20serious%20athlete%20working%20with%20coach%20on%20mechanics%2C%20premium%20sports%20science%20facility&width=1920&height=600&seq=elitedetail001&orientation=landscape',
    description: 'This program is a foundational building block to get your High School athlete ready for the demands of college. Both physically and mentally.',
    features: [
      'Everything included in Development tier',
      '12-week individualized training program tailored to your goals',
      'Advanced Rapsodo & TrackMan data analysis every session',
      'Mental performance coaching with certified specialist',
      'Showcase and recruiting preparation guidance',
      'Speed and agility integration for complete athleticism',
      'Monthly benchmark testing and progress tracking',
      'Direct coach communication via text and video review',
      'Priority scheduling and facility access',
      'Nutrition guidance and recovery protocols',
      'Video library access for remote learning',
      'Post-program transition plan',
    ],
    whoThisIsFor: {
      title: 'Who This Is For',
      description: 'Built for dedicated athletes who are serious about competing at the college or professional level and are ready to commit to elite-level training.',
      idealFor: [
        'High school athletes preparing for college recruitment',
        'Players committed to 12 weeks of intensive training',
        'Athletes who want access to pro-level technology and coaching',
        'Players preparing for showcases, combines, or tryouts',
        'Serious competitors looking to maximize their potential',
        'Athletes who need mental performance development alongside physical training',
      ],
    },
    whatToExpect: {
      title: 'What to Expect',
      description: 'A comprehensive 12-week journey designed to transform your game through systematic development and elite-level training.',
      breakdown: [
        {
          phase: 'Weeks 1-2: Assessment & Program Design',
          duration: '2 weeks',
          focus: [
            'Comprehensive assessment with all metrics',
            'Biomechanical analysis and video breakdown',
            'Goal setting and program customization',
            'Baseline testing across all performance areas',
            'Introduction to mental performance training',
          ],
        },
        {
          phase: 'Weeks 3-6: Foundation Building',
          duration: '4 weeks',
          focus: [
            'Mechanical refinement and consistency',
            'Strength and power development',
            'Velocity and command progression',
            'Speed and agility integration',
            'Mental skills practice and application',
          ],
        },
        {
          phase: 'Weeks 7-10: Performance Enhancement',
          duration: '4 weeks',
          focus: [
            'Advanced pitch design and repertoire development',
            'Peak velocity and performance training',
            'Competitive simulation and pressure situations',
            'Showcase preparation and strategy',
            'Mid-program benchmark testing',
          ],
        },
        {
          phase: 'Weeks 11-12: Peak & Transition to on going development',
          duration: '2 weeks',
          focus: [
            'Final benchmark testing and assessment',
            'Performance validation and showcase readiness',
            'Transition plan for continued development',
            'Recruiting strategy and next steps',
            'Program review and future recommendations',
          ],
        },
      ],
    },
    additionalBenefits: [
      'Unlimited access to training facility during program',
      'Weekly video analysis and remote coaching feedback',
      'Recruiting profile assistance and highlight video creation',
      'Access to college coach network and showcase opportunities',
      'Nutrition consultation and meal planning guidance',
      'Recovery protocols including mobility and arm care',
      'Alumni network access for continued support',
      'Discounted rates on future training blocks',
    ],
  },
  'college-pro': {
    id: 'college-pro',
    name: 'College / Pro Prep',
    subtitle: 'Full-Season Program',
    price: 'Contact Us',
    priceNote: 'Custom pricing available',
    heroImage: 'https://readdy.ai/api/search-image?query=Professional%20baseball%20training%20at%20highest%20level%2C%20elite%20athlete%20working%20with%20performance%20coach%20using%20advanced%20analytics%20and%20technology%2C%20premium%20sports%20facility%20with%20professional%20grade%20equipment%2C%20serious%20training%20environment%20with%20data%20screens%20and%20modern%20technology%2C%20high%20performance%20athletic%20development&width=1920&height=600&seq=collegeprodetail001&orientation=landscape',
    description: 'The ultimate training experience for athletes at the highest level. This full-season program is designed for advanced high school, college and professional prospects who need year-round development, advanced programming, and comprehensive support to reach their peak performance and advance their career.',
    features: [
      'Everything included in Elite tier',
      'Full-season periodization plan customized to your schedule',
      'Pro-level metrics and comprehensive reporting',
      'Recruiting and agent coordination support',
      'Nutrition and recovery guidance from specialists',
      'Unlimited session access throughout the season',
      'Video library and remote coaching support',
      'Dedicated performance coach assigned to you',
      'In-season and off-season programming',
      'Travel support and remote programming when needed',
      'Priority access to all facility resources',
      'Career development and professional guidance',
    ],
    whoThisIsFor: {
      title: 'Who This Is For',
      description: 'Reserved for elite athletes who are Advanced high school players, college level or preparing for professional opportunities and need year-round comprehensive support.',
      idealFor: [
        'Advanced high school and College players looking to advance to professional level',
        'Professional prospects preparing for draft or tryouts',
        'Athletes returning from injury who need comprehensive rehabilitation and return-to-play',
        'Players who are wanting year-round programming and support',
        'Elite athletes requiring dedicated coaching and individualized attention',
        'Players navigating recruiting, draft, or professional contract decisions',
      ],
    },
    whatToExpect: {
      title: 'What to Expect',
      description: 'A fully customized, year-round training program that adapts to your competitive schedule and provides comprehensive support for every aspect of your development and career.',
      breakdown: [
        {
          phase: 'Off-Season: Build Phase',
          duration: '8-12 weeks',
          focus: [
            'Comprehensive assessment and program design',
            'Maximum strength and power development',
            'Velocity and performance enhancement',
            'Mechanical refinement and optimization',
            'Mental performance and competitive mindset',
            'Recruiting or professional preparation',
          ],
        },
        {
          phase: 'Pre-Season: Peak Phase',
          duration: '4-6 weeks',
          focus: [
            'Competition-specific preparation',
            'Peak performance and readiness',
            'Game simulation and competitive scenarios',
            'Final mechanical adjustments',
            'Mental preparation and visualization',
            'Showcase or tryout preparation',
          ],
        },
        {
          phase: 'In-Season: Maintenance Phase',
          duration: 'Ongoing',
          focus: [
            'Performance maintenance and recovery',
            'In-season arm care and injury prevention',
            'Video review and mechanical adjustments',
            'Mental performance support',
            'Remote coaching and programming',
            'Travel support and consultation',
          ],
        },
        {
          phase: 'Post-Season: Recovery & Transition',
          duration: '2-4 weeks',
          focus: [
            'Active recovery and regeneration',
            'Season review and performance analysis',
            'Goal setting for next season',
            'Injury assessment and rehabilitation if needed',
            'Career planning and next steps',
            'Off-season program design',
          ],
        },
      ],
    },
    additionalBenefits: [
      'Unlimited facility access; when available to train at the facilty',
      'Direct phone and text access to your dedicated coach',
      'Bi-Weekly video analysis and remote coaching',
      'Nutrition consultation and customized meal plans',
      'Recovery protocols including strength and mobility work',
      'Agent and professional contract consultation',
      'College coach and scout network connections',
      'Professional showcase and tryout coordination',
      'Travel programming and remote support',
      'Family communication and support',
      'Alumni network and mentorship opportunities',
      'A place that is your enviroment!',
    ],
  },
  summer: {
    id: 'summer',
    name: 'Elite Summer Athlete Program',
    subtitle: 'High-Intensity Seasonal Training',
    price: '$1,500',
    priceNote: 'Per Month | 1-3 Month Program',
    heroImage: 'https://readdy.ai/api/search-image?query=Elite%20baseball%20training%20facility%20summer%20program%20with%20athletes%20doing%20explosive%20plyometric%20drills%20and%20speed%20training%20on%20professional%20turf%2C%20bright%20energetic%20morning%20training%20session%20with%20coaches%20supervising%20high%20performance%20development%2C%20modern%20sports%20facility%20with%20advanced%20equipment%20and%20dynamic%20athletic%20movements%2C%20intense%20focused%20training%20environment&width=1920&height=600&seq=summerdetail001&orientation=landscape',
    description: 'The Elite Summer Athlete Program is designed for baseball players who are serious about competing at the college and professional level. This is not a recreational camp, and it is not a traditional weight-room training program. Athletes train using a high-performance development system focused on the qualities that actually drive baseball performance.',
    features: [
      'Monday – Thursday training schedule',
      'Two sessions per day: 8:00 AM – 4:00 PM',
      'Morning focus on physical development and explosive athleticism',
      'Afternoon focus on throwing development and skill set refinement',
      'Bi-weekly performance testing and metrics tracking',
      'Biomechanics-driven throwing development',
      'Advanced plyometrics and elastic power training',
      'Speed, acceleration, and Rate of Force Development training',
      'Competitive mindset and mental performance coaching',
      'Assistance in finding a summer team that fits the training schedule',
      'Midday recovery block with lunch and mobility work',
      'Limited roster for individualized attention',
    ],
    whoThisIsFor: {
      title: 'Who This Is For',
      description: 'Built for high school eligible athletes to Free Agents who are serious about competing at the college and professional level and ready to commit to an elite development program.',
      idealFor: [
        'High school eligible baseball players preparing for college recruitment',
        'Athletes committed to 1-3 months of intensive daily training',
        'Players who want to train in a high-performance environment, not a recreational camp',
        'Athletes serious about measurable performance outcomes and objective metrics',
        'Players looking for a program with a proven track record of college and professional placement',
        'Athletes who need assistance finding a summer team that fits an elite training schedule',
      ],
    },
    whatToExpect: {
      title: 'What to Expect',
      description: 'A structured, high-performance training program that develops explosive athletic ability in the morning and throwing performance in the afternoon, Monday through Thursday.',
      breakdown: [
        {
          phase: 'Morning Session: Athletic Development Block',
          duration: '8:00 AM – 12:00 PM',
          focus: [
            'Competitive Mindset Meeting — leadership, accountability, visualization, and professional habits',
            'Speed & Acceleration Development — sprint mechanics, first-step explosiveness, reaction training',
            'Rate of Force Development Training— vertical jump, broad jump, reactive jump, ground contact efficiency',
            'Plyometric & Elastic Power Development — reactive jumps, multi-directional plyometrics, rotational, elastic rebound drills',
            'Proprioceptive stability training and dynamic movement quality',
            'Training designed to activate the nervous system and build explosive force production',
          ],
        },
        {
          phase: 'Midday Recovery Block',
          duration: '12:00 PM – 1:00 PM',
          focus: [
            'Lunch Break',
            'Hydration and nutrition guidance',
            'Mobility work and recovery protocols',
            'Nervous system reset before afternoon throwing session',
          ],
        },
        {
          phase: 'Afternoon Session: Throwing Development',
          duration: '1:00 PM – 4:00 PM',
          focus: [
            'Arm preparation and care protocols',
            'Progressive throwing programs tailored to position',
            'Mechanical efficiency development and velocity transfer drills',
            'Medicine ball mound work for rotational force production',
            'Ground-to-torso sequencing and elastic trunk rotation training',
            'Pitching impulse mechanics connecting morning athletic development to throwing',
            'Structured throwing groups for proper workload management',
          ],
        },
      ],
    },
    additionalBenefits: [
      '100% college placement rate for athletes who train in the program for two years or more',
      'Professional athletes developed, including players currently competing in professional baseball',
      'Bi-monthly performance testing tracking explosive athletic metrics',
      'Training system designed specifically for baseball\'s rotational and elastic demands',
      'Assistance in finding a summer team that fits the training schedule',
      'Training methods include advanced plyometrics, spiral force development, KAOS bags, waterbags, medicine balls',
      'Biomechanics, physics, and measurable performance outcomes drive every aspect of training',
      'High-performance environment built on objective performance and real outcomes',
      'Limited roster ensures individualized attention and coaching',
      'Not a recreational camp — an in-depth advanced program for serious athletes',
    ],
  },
};

export default function PackageDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const packageInfo = id ? packageData[id] : null;

  useEffect(() => {
    if (!packageInfo) return;
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-package-detail';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": `${packageInfo.name} | Elite Performance Lab`,
      "description": packageInfo.description,
      "url": `${import.meta.env.VITE_SITE_URL}/package/${packageInfo.id}`,
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": import.meta.env.VITE_SITE_URL },
          { "@type": "ListItem", "position": 2, "name": "Pricing", "item": `${import.meta.env.VITE_SITE_URL}/pricing` },
          { "@type": "ListItem", "position": 3, "name": packageInfo.name, "item": `${import.meta.env.VITE_SITE_URL}/package/${packageInfo.id}` }
        ]
      },
      "mainEntity": {
        "@type": "Service",
        "name": packageInfo.name,
        "description": packageInfo.description,
        "provider": {
          "@type": "Organization",
          "name": "Elite Performance Lab",
          "address": { "@type": "PostalAddress", "addressLocality": "Corsicana", "addressRegion": "TX", "addressCountry": "US" }
        },
        "offers": {
          "@type": "Offer",
          "price": packageInfo.price.replace(/[^0-9]/g, '') || "0",
          "priceCurrency": "USD",
          "description": packageInfo.priceNote
        }
      }
    });
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById('schema-package-detail');
      if (el) el.remove();
    };
  }, [packageInfo]);

  if (!packageInfo) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-black text-white mb-4">Package Not Found</h1>
          <button
            onClick={() => navigate('/pricing')}
            className="px-8 py-3 bg-teal-500 text-white font-semibold rounded-xl hover:bg-teal-400 transition-all cursor-pointer whitespace-nowrap"
          >
            Back to Pricing
          </button>
        </div>
      </div>
    );
  }

  const goToContact = () => {
    navigate('/');
    setTimeout(() => {
      const el = document.getElementById('contact');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  return (
    <div className="min-h-screen bg-gray-900">
      <Navigation />

      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <img
          src={packageInfo.heroImage}
          alt={packageInfo.name}
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/60 to-gray-900"></div>
        <div className="relative z-10 max-w-5xl mx-auto px-6">
          <button
            onClick={() => navigate('/pricing')}
            className="flex items-center gap-2 text-teal-400 hover:text-teal-300 transition-colors mb-8 cursor-pointer group"
          >
            <i className="ri-arrow-left-line text-xl group-hover:-translate-x-1 transition-transform"></i>
            <span className="font-semibold">Back to Pricing</span>
          </button>

          <div className="text-center">
            <p className="text-teal-400 text-sm font-semibold tracking-widest uppercase mb-4">
              {packageInfo.subtitle}
            </p>
            <h1 className="text-5xl lg:text-6xl font-black text-white mb-6 leading-tight">
              {packageInfo.name}
            </h1>
            <div className="mb-6">
              <div className="text-4xl font-black text-teal-400 mb-2">{packageInfo.price}</div>
            </div>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto leading-relaxed">
              {packageInfo.description}
            </p>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-3xl lg:text-4xl font-black text-white mb-4">What's Included</h2>
            <div className="w-20 h-1 bg-teal-500 mx-auto mb-6"></div>
            <p className="text-gray-300 text-lg max-w-3xl mx-auto leading-relaxed">
              {packageInfo.description}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {packageInfo.features.map((feature, i) => (
              <div key={i} className="flex items-start gap-4 bg-gray-800 rounded-xl p-5 border border-gray-700">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-checkbox-circle-fill text-teal-400 text-xl"></i>
                </div>
                <span className="text-gray-300 text-sm leading-relaxed">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="py-20 bg-gray-800">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-3xl lg:text-4xl font-black text-white mb-4">
              {packageInfo.whoThisIsFor.title}
            </h2>
            <div className="w-20 h-1 bg-teal-500 mx-auto mb-6"></div>
            <p className="text-gray-300 text-lg max-w-3xl mx-auto leading-relaxed">
              {packageInfo.whoThisIsFor.description}
            </p>
          </div>

          <div className="bg-gray-900 rounded-2xl p-8 border border-gray-700">
            <h3 className="text-xl font-bold text-white mb-6">Ideal For:</h3>
            <div className="space-y-4">
              {packageInfo.whoThisIsFor.idealFor.map((item, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-user-star-line text-teal-400 text-lg"></i>
                  </div>
                  <span className="text-gray-300 text-sm leading-relaxed">{item}</span>
                </div>
              ))}
            </div>

            <div className="mt-8 pt-6 border-t border-gray-700 flex justify-center">
              <button
                onClick={goToContact}
                className="inline-flex items-center gap-3 px-10 py-4 bg-teal-500 hover:bg-teal-400 text-white font-bold text-base rounded-full transition-all shadow-lg shadow-teal-500/25 hover:shadow-teal-400/40 hover:scale-105 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-calendar-check-line text-lg"></i>
                Reserve Now
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-3xl lg:text-4xl font-black text-white mb-4">
              {packageInfo.whatToExpect.title}
            </h2>
            <div className="w-20 h-1 bg-teal-500 mx-auto mb-6"></div>
            <p className="text-gray-300 text-lg max-w-3xl mx-auto leading-relaxed">
              {packageInfo.whatToExpect.description}
            </p>
          </div>

          <div className="space-y-6">
            {packageInfo.whatToExpect.breakdown.map((phase, i) => {
              // Insert Athletic Testing Card as item #3 (after index 1)
              const displayNumber = packageInfo.athleticTesting
                ? i < 2 ? i + 1 : i + 2
                : i + 1;

              return (
                <>
                  <div
                    key={i}
                    className="bg-gray-800 rounded-2xl p-7 border border-gray-700 hover:border-teal-500/50 transition-all"
                  >
                    <div className="flex items-start gap-5 mb-5">
                      <div className="w-12 h-12 flex items-center justify-center flex-shrink-0 bg-teal-500/10 rounded-xl">
                        <span className="text-2xl font-black text-teal-400">{displayNumber}</span>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-white mb-1">{phase.phase}</h3>
                        <p className="text-teal-400 text-sm font-medium">{phase.duration}</p>
                      </div>
                    </div>

                    <div className="pl-17">
                      <ul className="space-y-3">
                        {phase.focus.map((item, fi) => (
                          <li key={fi} className="flex items-start gap-3">
                            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <i className="ri-arrow-right-s-line text-teal-400 text-base"></i>
                            </div>
                            <span className="text-gray-300 text-sm leading-relaxed">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* Athletic Testing Card inserted as #3 after second phase */}
                  {i === 1 && packageInfo.athleticTesting && (
                    <div
                      key="athletic-testing"
                      className="bg-gray-800 rounded-2xl p-7 border border-gray-700 hover:border-teal-500/50 transition-all"
                    >
                      <div className="flex items-start gap-5 mb-5">
                        <div className="w-12 h-12 flex items-center justify-center flex-shrink-0 bg-teal-500/10 rounded-xl">
                          <span className="text-2xl font-black text-teal-400">3</span>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-white mb-1">{packageInfo.athleticTesting.title}</h3>
                          <p className="text-teal-400 text-sm font-medium">{packageInfo.athleticTesting.subtitle}</p>
                        </div>
                      </div>

                      <div className="pl-17">
                        <div className="grid md:grid-cols-3 gap-4">
                          {packageInfo.athleticTesting.metrics.map((metric, mi) => (
                            <div key={mi} className="bg-gray-900/60 rounded-xl p-5 border border-gray-700 flex flex-col gap-3">
                              <div className="w-10 h-10 flex items-center justify-center bg-teal-500/10 rounded-xl flex-shrink-0">
                                <i className={`${metric.icon} text-teal-400 text-xl`}></i>
                              </div>
                              <div>
                                <h4 className="text-white font-bold text-base mb-1">{metric.name}</h4>
                                <p className="text-gray-400 text-sm leading-relaxed mb-3">{metric.description}</p>
                                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-teal-400 bg-teal-500/10 px-3 py-1 rounded-full">
                                  <i className="ri-ruler-line text-xs"></i>
                                  {metric.unit}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              );
            })}
          </div>
        </div>
      </section>

      {/* Additional Benefits */}
      <section className="py-20 bg-gray-800">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-3xl lg:text-4xl font-black text-white mb-4">Additional Benefits</h2>
            <div className="w-20 h-1 bg-teal-500 mx-auto"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {packageInfo.additionalBenefits.map((benefit, i) => (
              <div key={i} className="flex items-start gap-4 bg-gray-900 rounded-xl p-5 border border-gray-700">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-gift-line text-teal-400 text-lg"></i>
                </div>
                <span className="text-gray-300 text-sm leading-relaxed">{benefit}</span>
              </div>
            ))}
          </div>

          <div className="mt-10 flex justify-center">
            <button
              onClick={goToContact}
              className="inline-flex items-center gap-3 px-10 py-4 bg-teal-500 hover:bg-teal-400 text-white font-bold text-base rounded-full transition-all shadow-lg shadow-teal-500/25 hover:shadow-teal-400/40 hover:scale-105 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-calendar-check-line text-lg"></i>
              Reserve Now
            </button>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-teal-700 to-teal-500">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-black text-white mb-4">Ready to Get Started?</h2>
          <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
            Take the next step in your athletic journey. Contact us today to schedule your session or learn more about this program.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <button
              onClick={goToContact}
              className="px-10 py-4 bg-white text-teal-700 text-base font-bold rounded-full hover:bg-gray-100 transition-all shadow-2xl cursor-pointer whitespace-nowrap"
            >
              Contact Us Now
            </button>
            <button
              onClick={() => navigate('/pricing')}
              className="px-10 py-4 bg-transparent text-white text-base font-bold rounded-full border-2 border-white hover:bg-white hover:text-teal-700 transition-all cursor-pointer whitespace-nowrap"
            >
              View All Packages
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}